import * as React from 'react'
import {createStyles, Grid, Paper, Theme, withStyles, WithStyles, withTheme,} from "@material-ui/core";
import {inject, observer} from "mobx-react";
import UserStore from "../../stores/UserStore";
import AccountStore from "../../stores/AccountStore";
import Account from "../../model/Account"
import {action, observable, when} from "mobx";
import Progress from "../../components/Progress";
import CourseStore from "../../stores/CourseStore";
import Notify from "../../components/notify/Notify";
import Activity from "../../model/Activity";
import {getISODateFromDate} from "../../stores/StoreUtilities";
import {format} from "date-fns";
import MaterialTable from "material-table";
import {ExportCsv, ExportPdf} from '@material-table/exporters';
import {ActivityType, ModelActivityFilterInput, ModelSortDirection, ModelStringKeyConditionInput} from "../../API";
import DatePickerValidator from "../../components/form/DatePickerValidator";
import FormValidator from "../../components/form/FormValidator";
import TitleButton from "../../components/TitleButton";
import SearchIcon from '@material-ui/icons/Search';
import ExportIcon from '@material-ui/icons/GetApp';

const styles = (theme: Theme) => createStyles({
  root: {
    display: "flex",
    flexGrow: 1,
    justifyContent: 'top',
    alignItems: 'center',
  },
  paper: {
    width: "100%",
    height: "100%", // "calc(100vh - 200px)"
  },
  toolBar: {
    padding: theme.spacing(2),
    paddingRight: theme.spacing(4),
    width: "100%"
  },
  title: {
    fontSize: 24,
    fontWeight: 800
  },
  range: {
    display: "flex",
    justifyContent: "flex-end",
    alignContent: "center"
  }
})

interface IAccountComplianceReportProps {
  userStore?: UserStore
  accountStore?: AccountStore
  courseStore?: CourseStore
  progress?: Progress
  notify?: Notify
}

interface IData {
  name: string
  courseTitle: string
  lessonTitle: string
  standards: string
  completionDate: string
  hours: number
  lessonStatus: string
}

@inject("userStore", "accountStore", "courseStore", "progress", "notify")
@observer
class AccountComplianceReport extends React.Component<WithStyles<typeof styles> & IAccountComplianceReportProps> {

  @observable account?: Account
  @observable activity: Activity[] = []
  @observable data: IData[] = []
  @observable isLoading = false
  today = new Date()
  @observable values = {
    startDate: "",
    endDate: "",
  }

  columns: any[] = [
    {title: 'Student', field: 'name' },
    {title: 'Course', field: 'courseTitle'},
    {title: 'Lesson', field: 'lessonTitle'},
    {title: 'Standards', field: 'standards'},
    {title: 'Completed', field: 'completionDate'},
    {title: "Hours", field: 'hours' },
    {title: "Status", field: 'status'}
]

  componentDidMount () {
    const { userStore, accountStore} = this.props
    when(
      () => !userStore!.isLoading,
      async () => {
        this.account = accountStore!.account
      }
    )
  }

  render() {
    const { classes } = this.props

    const toolbar = <Grid container direction="row" className={classes.toolBar}>
      <Grid item xs={4} className={classes.title}>
        Compliance Report
      </Grid>
      <Grid item xs={8} className={classes.range}>
        <FormValidator autoComplete="off">
          <Grid container direction="row" spacing={1}>
            <Grid item>
              <DatePickerValidator
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                placeholder="MM/dd/yyyy"
                margin="none"
                autoOk
                id="startDate"
                label="Start Date *"
                name="startDate"
                value={this.values.startDate}
                required
                style={{width:180}}
                onChange={this.onChange}
              />
            </Grid>
            <Grid item>
              <DatePickerValidator
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                placeholder="MM/dd/yyyy"
                margin="none"
                autoOk
                id="endDate"
                label="End Date *"
                name="endDate"
                value={this.values.endDate}
                required
                style={{width:180}}
                onChange={this.onChange}
              />
            </Grid>
            <Grid item style={{paddingTop:20}}>
              <TitleButton icon={<SearchIcon/>}
                           onClick={this.onSubmit}
                           disabled={!this.isValid}/>
            </Grid>
            <Grid item style={{paddingTop:20}}>
              <TitleButton icon={<ExportIcon/>}
                           onClick={this.onExportCsv}
                           disabled={this.data.length <= 0}/>
            </Grid>
          </Grid>
        </FormValidator>
      </Grid>
    </Grid>

    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <MaterialTable
            title="Compliance Report"
            columns={this.columns}
            data={this.data}
            options={{
              showTitle: true,
              exportButton: true,
              search: false,
              sorting: true,
              filtering: true,
              padding: "dense",
              pageSize: 100,
              pageSizeOptions: [10,25,50,100,250,500,1000],
              maxBodyHeight: "calc(100vh - 325px)",
            }}
            components={{
              Toolbar: props => (
                toolbar
              ),
            }}
          />
        </Paper>
      </div>
    )
  }

  get isValid(): boolean {
    return (this.account !== undefined &&
      this.values.startDate !== undefined && this.values.startDate !== null && this.values.startDate !== "" &&
      this.values.endDate !== undefined && this.values.endDate !== null && this.values.endDate !== "")
  }

  onSubmit = async () => {
    if (this.isValid) {
      this.loadActivity()
    }
  }

  onExportCsv = async () => {
    const { userStore } = this.props

    const startDate = format(new Date(this.values.startDate), "MM-dd-yyyy")
    const endDate = format(new Date(this.values.endDate), "MM-dd-yyyy")
    const prefix = (userStore!.isAdmin) ? this.account!.name : "HazmatAndRescue"
    const filename = `${prefix} Compliance Report for ${startDate} to ${endDate}`
    ExportCsv(this.columns, this.data, filename)
  }

  onExportPdf = async () => {
    ExportPdf(this.columns, this.data, 'Compliance Report')
  }

  @action onChange = (event: any) => {
    const name = event.target.name
    this.values[name] = event.target.value
  }

  loadActivity = async () => {
    const { userStore, accountStore, progress } = this.props
    this.isLoading = true
    progress!.show("AccountComplianceReport")
    const data: any[] = []
    const accountId = this.account!.id
    const startDate = getISODateFromDate(new Date(this.values.startDate))
    const endDate = getISODateFromDate(new Date(this.values.endDate))
    const activityDate: ModelStringKeyConditionInput = {
      between: [startDate, endDate]
    }
    const filter: ModelActivityFilterInput = {
      or: [
        { activityType: {eq: ActivityType.LessonComplete} },
        { activityType: {eq: ActivityType.ClassComplete} },
      ]
    }
    if (userStore!.isStudent || accountStore!.isIndividual) {
      // Restrict for individual student
      filter.userId = {eq: userStore!.user!.id}
    }

    const activity = await accountStore!.listActivityByAccount(accountId, activityDate, ModelSortDirection.ASC, filter)
    activity.sort((a: Activity, b: Activity) => a.createdAt.localeCompare(b.createdAt))
    activity.forEach((a: Activity) => {
      if (a.values) {
        const v = JSON.parse(a.values)
        if (a.activityType === ActivityType.LessonComplete) {
          data.push({
            name: `${v.lastName}, ${v.firstName}`,
            courseTitle: v.courseTitle,
            lessonTitle: v.lessonTitle ?? "",
            standards: v.standards ?? v.complianceCodes,
            completionDate: format(new Date(v.completionDate), "MM/dd/yyyy"),
            hours: isNaN(v.duration) ? 0 : Math.round(v.duration / 36) / 100,
            status: v.lessonStatus
          })
        } else {
          data.push({
            name: `${v.lastName}, ${v.firstName}`,
            courseTitle: v.courseTitle,
            lessonTitle: "Completed",
            standards: "",
            completionDate: format(new Date(v.completionDate), "MM/dd/yyyy"),
            hours: v.creditHours ?? 0,
            status: v.classStatus
          })
        }
      }
    })
    progress!.hide("AccountComplianceReport")
    this.isLoading = false
    this.data = data
  }

}

export default withTheme((withStyles(styles)(AccountComplianceReport)))